import React from "react";
import "./SunScreenTab.css";
import Text from "src/components/Text/Text";
import { useConfiguration } from "src/contexts/ConfigurationContext";
import HorizontalSwitch from "src/components/Input/HorizontalSwitch/HorizontalSwitch";
import RadioBlocksExtended from "src/components/Input/RadioBlocksExtended/RadioBlocksExtended";
import { Lights, SunScreen } from "src/types/configuration";
// @ts-ignore
import isEqual from "lodash/isEqual";
import SelectUnselectExtended from "src/components/Input/SelectUnselectExtended/SelectUnselectExtended";
import { initialConfiguration } from "src/hooks/useConfigurationState";

const sunScreenColors = [
  {
    id: 0,
    name: "Standaard Grijs",
    // imageUrl: "./images/poer.png",
    color: "#eeeeee",
    value: "default",
    price: 0,
    carouselImages: [`${process.env.PUBLIC_URL}/images/default.jpg`],
    description:
      "",
  },
  {
    id: 1,
    name: "LightIvory, RAL 1015",
    // imageUrl: "./images/poer.png",
    color: "#e6d2b5",
    value: "sand",
    price: 0,
    carouselImages: [`${process.env.PUBLIC_URL}/images/ral1015.jpg`],
    description:
      "",
  },
  {
    id: 2,
    name: "PastelBlue, RAL 5024",
    // imageUrl: "./images/poer.png",
    color: "#6093ac",
    value: "sky",
    price: 0,
    carouselImages: [`${process.env.PUBLIC_URL}/images/ral1015.jpg`],
    description:
      "",
  },
  {
    id: 3,
    name: "TarpaulinGrey, RAL 7010",
    // imageUrl: "./images/poer.png",
    color: "#585c56",
    value: "dark",
    price: 0,
    carouselImages: [`${process.env.PUBLIC_URL}/images/ral5024.jpg`],
    description:
      "",
  },
  {
    id: 4,
    name: "AgateGrey, RAL 7038",
    // imageUrl: "./images/poer.png",
    color: "#B4B8B0",
    value: "gray",
    price: 0,
    carouselImages: [`${process.env.PUBLIC_URL}/images/ral7038.jpg`],
    description:
      "",
  },
  {
    id: 5,
    name: "BlackBrown, RAL 8022",
    // imageUrl: "./images/poer.png",
    color: "#211F20",
    value: "black",
    price: 0,
    carouselImages: [`${process.env.PUBLIC_URL}/images/ral8022.jpg`],
    description:
      "",
  },
];

const carouseltop = [
  `${process.env.PUBLIC_URL}/images/ver_sun_default.jpg`,
  `${process.env.PUBLIC_URL}/images/ver_sun_yellow.jpg`,
  `${process.env.PUBLIC_URL}/images/ver_sun_blue.jpg`,
  `${process.env.PUBLIC_URL}/images/ver_sun_darkgray.jpg`,
  `${process.env.PUBLIC_URL}/images/ver_sun_gray.jpg`,
  `${process.env.PUBLIC_URL}/images/ver_sun_black.jpg`,
];

const carouselbot = [
  `${process.env.PUBLIC_URL}/images/ver_sunund_default.jpg`,
  `${process.env.PUBLIC_URL}/images/ver_sunund_yellow.jpg`,
  `${process.env.PUBLIC_URL}/images/ver_sunund_blue.jpg`,
  `${process.env.PUBLIC_URL}/images/ver_sunund_darkgray.jpg`,
  `${process.env.PUBLIC_URL}/images/ver_sunund_gray.jpg`,
  `${process.env.PUBLIC_URL}/images/ver_sunund_black.jpg`,
];

const lights = [
  {
    id: 0,
    name: "Geen verlichting",
    value: "none",
    price: 0,
    // color: "#444444",
    imageUrl: `${process.env.PUBLIC_URL}/images/empty.png`,
    description:
      "",
  },
  {
    id: 1,
    name: "In elke dakligger",
    value: "all",
    price: 0,
    imageUrl: `${process.env.PUBLIC_URL}/images/ver_lights_all.png`,
    description:
      "",
  },
  {
    id: 2,
    name: "In elke 2e dakligger",
    value: "some",
    price: 0,
    imageUrl: `${process.env.PUBLIC_URL}/images/ver_lights_some.png`,
    description:
      "",
  },
];

const SunScreenTab: React.FC = () => {
  const { state, setSunScreen, setLights, configuration } = useConfiguration();

  // const { configurator } = useConfiguration();

  // Priced copies rather than writes into the shared module array, so the
  // surcharge cannot leak into anything else that reads `lights`.
  const lightBlocks = lights.map((light) => ({
    ...light,
    price: configuration.calculationService.getSurcharge(
      {
        lights: {
          ...state.lights,
          enabled: light.value !== "none",
          everyLigger: light.value === "all",
        },
      },
      {
        lights: {
          ...state.lights,
          enabled: initialConfiguration.lights.enabled,
        },
      }
    ),
  }));

  const handleSunScreenCoverChange = (value: string) => {
    if (state.sunScreen.cover !== value) {
      const temp = { ...state.sunScreen };
      temp.cover = value;
      setSunScreen({ ...temp });
    }
  };

  const getSunScreen = () => {
    const screen = state.sunScreen;
    return !screen.enabled ? "none" : screen.top ? "top" : "bottom";
  };

  const getLights = () => {
    const lights = state.lights;
    return !lights.enabled ? "none" : lights.everyLigger ? "all" : "some";
  };

  const handleSunScreen = (value: string) => {
    let temp: SunScreen = { ...state.sunScreen };

    if (value === "top")
      temp = { ...state.sunScreen, enabled: true, top: true };
    else if (value === "bottom")
      temp = { ...state.sunScreen, enabled: true, top: false };
    else temp = { ...state.sunScreen, enabled: false };

    if (!isEqual(state.sunScreen, temp)) setSunScreen({ ...temp });
  };

  const handleLights = (value: string) => {
    let temp: Lights = { ...state.lights };

    if (value === "all")
      temp = {
        ...state.lights,
        enabled: true,
        everyLigger: true,
        lightsOn: true,
      };
    else if (value === "some")
      temp = {
        ...state.lights,
        enabled: true,
        everyLigger: false,
        lightsOn: true,
      };
    else temp = { ...state.lights, enabled: false, lightsOn: false };

    if (!isEqual(state.lights, temp)) setLights({ ...temp });
  };

  const SunScreenComponent = () => {
    const sunScreens = [
      {
        id: 0,
        name: "Geen zonwering",
        value: "none",
        imageUrl: `${process.env.PUBLIC_URL}/images/empty.png`,
        price: 0,
        description:
          "",
      },
      {
        id: 1,
        name: "Bovenop het dak",
        imageUrl: `${process.env.PUBLIC_URL}/images/ver_sunscreen_top.png`,
        value: "top",
        price: 0,
        carouselImages: carouseltop,
        description:
          "Bovendakzonnewering houdt de zon tegen voordat deze het glas bereikt. Hierdoor blijft de temperatuur onder uw veranda of terrasoverkapping aangenaam en wordt hinderlijke warmte effectief verminderd, terwijl u volop kunt genieten van natuurlijk daglicht.",

        component: () => (
          <ColorComponent parentIsSelected={getSunScreen() === "top"} />
        ),
      },
      {
        id: 2,
        name: "Onder het dak",
        imageUrl: `${process.env.PUBLIC_URL}/images/ver_sunscreen_bottom.png`,
        value: "bottom",
        price: 0,
        carouselImages: carouselbot,
        description:
          "Onderdakzonnewering biedt bescherming tegen zonlicht en hoge temperaturen. Het zorgt ook voor een comfortabele sfeer en geeft uw veranda een stijlvolle, afgewerkte uitstraling. U kunt hem ook met een gerust hart naar beneden houden als u van huis gaat.",

        component: () => (
          <ColorComponent parentIsSelected={getSunScreen() === "bottom"} />
        ),
      },
    ];

    sunScreens.map((item) => {
      item.price = configuration.calculationService.getSurcharge(
        {
          sunScreen: {
            ...state.sunScreen,
            enabled: item.value !== "none",
            top: item.value === "top",
          },
        },
        {
          sunScreen: {
            ...state.sunScreen,
            enabled: initialConfiguration.sunScreen.enabled,
          },
        }
      );
    });

    const handleSunScreenColorChange = (value: string) => {
      if (state.sunScreen.cover !== value) {
        const temp = { ...state.sunScreen };
        temp.color = value;
        setSunScreen({ ...temp });
      }
    };

    const ColorComponent = ({
      parentIsSelected,
    }: {
      parentIsSelected: boolean;
    }) => {
      return (
        <>
          {/* <Text content="Bedekkingsgraad" /> */}
          <Text content="Zonwering gesloten tonen in 3D?" />
          <HorizontalSwitch
            value={state.sunScreen.cover}
            values={[
              { id: 0, label: " 0%", value: "0" },
              { id: 1, label: " 50%", value: "50" },
              { id: 2, label: " 100%", value: "100" },
            ]}
            onChange={handleSunScreenCoverChange}
          />
          <RadioBlocksExtended
            blocks={sunScreenColors}
            value={state.sunScreen.color}
            onChange={handleSunScreenColorChange}
            className={"inner_child"}
            parentIsSelected={parentIsSelected}
          />
        </>
      );
    };

    return (
      <>
        <RadioBlocksExtended
          blocks={sunScreens}
          value={getSunScreen()}
          onChange={handleSunScreen}
        />
        {/* {state.sunScreen.enabled && (
          <>
            <Text content="Coverage" />

            <HorizontalSwitch
              value={state.sunScreen.cover}
              values={[
                { id: 0, label: " 0%", value: "0" },
                { id: 1, label: " 50%", value: "50" },
                { id: 2, label: " 100%", value: "100" },
              ]}
              onChange={handleSunScreenCoverChange}
            />
          </>
        )} */}
      </>
    );
  };
  const LightComponent = () => {
    return (
      <>
        <RadioBlocksExtended
          blocks={lightBlocks}
          value={getLights()}
          onChange={handleLights}
          className="inner_child"
        />
      </>
    );
  };

  const handleSunScreenOn = (enabled: boolean) => {
    if (state.sunScreen.enabled !== enabled)
      setSunScreen({ ...state.sunScreen, enabled: enabled });
  };

  const handleLightsEnabled = (enabled: boolean) => {
    if (state.lights.enabled !== enabled)
      setLights({ ...state.lights, enabled: enabled });
  };

  return (
    <div className="sunscreen-tab">
      <SelectUnselectExtended
        name="Zonwering"
        deselectByCheckboxOnly={true}
        imageUrl={`${process.env.PUBLIC_URL}/images/ver_sunscreen_top.png`}
        description=""
        isSelected={state.sunScreen.enabled}
        onSelect={() => true}
        onUnselect={() => false}
        // componentIsVisible={state.sunScreen.enabled}
        Component={SunScreenComponent}
        carouselImages={[...carouseltop, ...carouselbot]}
        selectedNames={
          !state.sunScreen.top ? `Onder het dak` : `Bovenop het dak`
        }
      />
      <SelectUnselectExtended
        name="Verlichting"
        deselectByCheckboxOnly={true}
        imageUrl={`${process.env.PUBLIC_URL}/images/ver_lights_all.png`}
        description="Geïntegreerde LED-spots geven uw veranda een luxe uitstraling en zorgen voor sfeervolle verlichting, zodat u ook in de avonduren optimaal van uw buitenruimte kunt genieten. De energiezuinige verlichting wordt subtiel verwerkt in de constructie."
        isSelected={state.lights.enabled}
        // onSelect={() => true}
        onSelect={() => {
          // if (!state.lights.lightsOn)
          //   setLights({ ...state.lights, lightsOn: true });
        }}
        // onUnselect={() => true}
        onUnselect={() => {
          // setLights({ ...state.lights, lightsOn: false });
        }}
        // componentIsVisible={state.lights.enabled}
        Component={LightComponent}
        selectedNames={
          state.lights.everyLigger
            ? `In elke dakligger`
            : `In elke 2e dakligger`
        }
        carouselImages={lights
          .map((i) => i.imageUrl)
          .filter((i) => !i.includes("empty"))}
      />

      {/* <RadioBlocksExtended
        blocks={[
          {
            id: 1,
            name: "Verlichting",
            value: "screen",
            price: 0,
            imageUrl: `${process.env.PUBLIC_URL}/images/ver_lights_all.png`,
            description:
              "",
            component: LightComponent,
          },
        ]}
        value={" "}
        onChange={() => null}
        // className={"inner_child"}
      /> */}
    </div>
  );
};

export default SunScreenTab;
